import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';

import { apiClient } from '../api/client';
import { useApi } from '../api/useApi';
import { AsyncBoundary } from '../components/AsyncState';
import { BackLink } from '../components/layout/BackLink';
import { ChatTab } from '../components/stand/ChatTab';
import { GalleryTab } from '../components/stand/GalleryTab';
import { JobsTab } from '../components/stand/JobsTab';
import { WhyUsTab } from '../components/stand/WhyUsTab';
import { StandLogo } from '../components/StandLogo';
import { paths } from '../routes';
import { TreasureDrop } from '../treasure-hunt/TreasureDrop';
import { useTreasureHunt } from '../treasure-hunt/TreasureHuntProvider';
import { accentFor, accentText } from '../utils/pavilion-accent';

const TABS = ['jobs', 'why', 'gallery', 'chat'] as const;
type TabKey = (typeof TABS)[number];

function isTabKey(value: string | null): value is TabKey {
  return value !== null && (TABS as readonly string[]).includes(value);
}

export function StandPage(): ReactElement {
  const { t } = useTranslation();
  const { standSlug } = useParams<{ standSlug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hunt, entered: enteredHunt } = useTreasureHunt();

  const slug = standSlug ?? '';
  const standRequest = useApi(() => apiClient.getStand(slug), [slug]);
  const stand = standRequest.data;

  // Chat is offered only by stands given the chat feature; a `?tab=chat` link to
  // any other stand opens on jobs.
  const tabs = TABS.filter((tab) => tab !== 'chat' || stand?.hasChat === true);
  const requested = searchParams.get('tab');
  const activeTab: TabKey =
    isTabKey(requested) && (requested !== 'chat' || stand === undefined || stand.hasChat)
      ? requested
      : 'jobs';

  // Each tab fetches only when it is the visible one, so opening a stand does
  // not pull the profile and the offer list at once.
  const offers = useApi(
    () =>
      activeTab === 'jobs' && slug !== '' ? apiClient.getStandOffers(slug) : Promise.resolve([]),
    [slug, activeTab],
  );
  const profile = useApi(
    () =>
      activeTab === 'why' && slug !== ''
        ? apiClient.getStandProfile(slug)
        : Promise.resolve(undefined),
    [slug, activeTab],
  );

  // `from` names the pavilion the visitor came from by slug; a stand can sit in
  // several. The refs the stand already carries supply the name and position
  // the back link needs, so no pavilion request is made.
  const fromSlug = searchParams.get('from');
  const pavilion = stand?.pavilions.find((entry) => entry.slug === fromSlug) ?? stand?.pavilions[0];

  // Settled-and-missing, not merely absent: redirecting while the request is in
  // flight would bounce every deep link before its data arrived.
  if (!standRequest.loading && standRequest.error?.status === 404) {
    return <Navigate to="/" replace />;
  }

  // Returned early so the rest of the page can treat `stand` as present rather
  // than guarding every field.
  if (stand === undefined) {
    return (
      <div className="app-shell pb-12">
        <AsyncBoundary
          loading={standRequest.loading}
          error={standRequest.error}
          onRetry={standRequest.reload}
        >
          {null}
        </AsyncBoundary>
      </div>
    );
  }

  const selectTab = (tab: TabKey): void => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="app-shell pb-12">
      <BackLink
        to={pavilion === undefined ? paths.home : paths.category(pavilion.slug)}
        label={t('stand.backToExhibitors')}
        {...(pavilion !== undefined && {
          suffix: pavilion.name,
          suffixClassName: accentText(accentFor(pavilion.position)),
        })}
      />

      <header className="mt-2 mb-6 flex items-center gap-4">
        <StandLogo url={stand.logoUrl} className="size-16" />
        <h1 className="text-2xl font-bold text-fog-100">{stand.title}</h1>
      </header>

      <div
        role="tablist"
        aria-label={stand.title}
        className="mb-6 flex items-center justify-between gap-1 border-b border-ink-700"
      >
        {tabs.map((tab) => {
          const selected = tab === activeTab;

          return (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => {
                selectTab(tab);
              }}
              className={`relative -mb-px border-b-2 px-1 pb-3 text-[15px] transition-colors ${
                selected
                  ? 'border-fog-100 font-bold text-fog-100'
                  : 'border-transparent text-fog-500 hover:text-fog-300'
              }`}
            >
              {t(`stand.tabs.${tab}`)}
              {/* The dot marks a stand whose chat is staffed right now. */}
              {tab === 'chat' && stand.chatOnline && (
                <span
                  aria-hidden="true"
                  className="absolute -top-0.5 -right-2 size-2 rounded-full bg-online"
                />
              )}
            </button>
          );
        })}
      </div>

      <div role="tabpanel">
        {activeTab === 'jobs' && (
          <AsyncBoundary loading={offers.loading} error={offers.error} onRetry={offers.reload}>
            <JobsTab jobs={offers.data ?? []} {...(fromSlug !== null && { from: fromSlug })} />
          </AsyncBoundary>
        )}
        {activeTab === 'why' && (
          <AsyncBoundary loading={profile.loading} error={profile.error} onRetry={profile.reload}>
            {profile.data !== undefined && <WhyUsTab profile={profile.data} />}
          </AsyncBoundary>
        )}
        {activeTab === 'gallery' && <GalleryTab media={stand.media} />}
        {activeTab === 'chat' && <ChatTab stand={stand} />}
      </div>

      {hunt !== null && !enteredHunt && (
        <TreasureDrop
          key={`${String(stand.id)}:${activeTab}`}
          icons={hunt.icons}
          standId={stand.id}
        />
      )}
    </div>
  );
}
